import { useEffect, useSyncExternalStore } from 'react'
import { useParams } from 'next/navigation'
import { useMemoizedFn } from 'ahooks'
import enDictionary from '@/locales/en'
import { interpolate, Locale, isLangSupported } from '@/locales'
import { DEFAULT_LOCALE } from '@/constant/lang'
import { useI18nBootstrap } from '@/provider/I18nBootstrapProvider'
import { DictionaryCache } from '@/lib/i18n/DictionaryCache'

type Dictionary = Record<string, string>

const dictionaryCache = new DictionaryCache<Locale>()
dictionaryCache.set(DEFAULT_LOCALE as Locale, enDictionary as Dictionary, true)

const normalizeLocale = (lang: string): Locale => {
  if (isLangSupported(lang)) {
    return lang as Locale
  }
  return DEFAULT_LOCALE as Locale
}

export async function loadCompleteDictionary(
  locale: Locale
): Promise<Dictionary> {
  const normalizedLocale = normalizeLocale(locale)
  try {
    return await dictionaryCache.loadComplete(normalizedLocale, async () => {
      const mod = await import(`@/locales/${normalizedLocale}`)
      return mod.default as Dictionary
    })
  } catch (error) {
    console.error(
      `Failed to load locale dictionary: ${normalizedLocale}`,
      error
    )
    return (
      dictionaryCache.get(DEFAULT_LOCALE as Locale)?.dictionary ??
      ({} as Dictionary)
    )
  }
}

export function buildTranslator(locale: Locale) {
  const normalizedLocale = normalizeLocale(locale)

  if (!dictionaryCache.get(normalizedLocale)?.complete) {
    // 触发预加载，避免首次切换语言时文案回退
    void loadCompleteDictionary(normalizedLocale)
  }

  return function translate(
    key: string,
    replacements?: Record<string, string | number>
  ): string {
    const dictionary =
      dictionaryCache.get(normalizedLocale)?.dictionary ??
      dictionaryCache.get(DEFAULT_LOCALE as Locale)?.dictionary

    const text = dictionary?.[key]
    if (!text) return String(key)
    if (!replacements) return text
    return interpolate(text, replacements)
  }
}

export function useTranslator(locale: Locale) {
  const normalizedLocale = normalizeLocale(locale)
  const bootstrap = useI18nBootstrap()
  const bootstrapLang = bootstrap ? normalizeLocale(bootstrap.lang) : null
  const bootstrapDictionary =
    bootstrapLang === normalizedLocale ? bootstrap?.dictionary : null
  const bootstrapComplete =
    bootstrapLang === normalizedLocale ? (bootstrap?.complete ?? true) : true

  useSyncExternalStore(
    dictionaryCache.subscribe,
    dictionaryCache.getVersion,
    dictionaryCache.getVersion
  )

  useEffect(() => {
    if (bootstrapDictionary) {
      dictionaryCache.set(
        normalizedLocale,
        bootstrapDictionary as Dictionary,
        bootstrapComplete
      )
    }
  }, [bootstrapComplete, bootstrapDictionary, normalizedLocale])

  useEffect(() => {
    if (bootstrapDictionary && !bootstrapComplete) return
    void loadCompleteDictionary(normalizedLocale)
  }, [bootstrapComplete, bootstrapDictionary, normalizedLocale])

  return useMemoizedFn(
    (key: string, replacements?: Record<string, string | number>): string => {
      const dictionary =
        dictionaryCache.get(normalizedLocale)?.dictionary ??
        (bootstrapDictionary as Dictionary | null) ??
        dictionaryCache.get(DEFAULT_LOCALE as Locale)?.dictionary ??
        ({} as Dictionary)

      const text = dictionary[key]
      if (!text) return String(key)
      if (!replacements) return text
      return interpolate(text, replacements)
    }
  )
}

// 客户端 hook
export default function useI18n() {
  const params = useParams()
  const lang = normalizeLocale(String(params.lang ?? DEFAULT_LOCALE))
  const bootstrap = useI18nBootstrap()
  const bootstrapLang = bootstrap ? normalizeLocale(bootstrap.lang) : null
  const bootstrapDictionary =
    bootstrapLang === lang ? bootstrap?.dictionary : null
  const bootstrapComplete =
    bootstrapLang === lang ? (bootstrap?.complete ?? true) : true

  useSyncExternalStore(
    dictionaryCache.subscribe,
    dictionaryCache.getVersion,
    dictionaryCache.getVersion
  )

  useEffect(() => {
    if (bootstrapDictionary) {
      dictionaryCache.set(
        lang,
        bootstrapDictionary as Dictionary,
        bootstrapComplete
      )
    }
  }, [bootstrapComplete, bootstrapDictionary, lang])

  useEffect(() => {
    if (bootstrapDictionary && !bootstrapComplete) return
    void loadCompleteDictionary(lang)
  }, [bootstrapComplete, bootstrapDictionary, lang])

  const dictionary =
    dictionaryCache.get(lang)?.dictionary ??
    (bootstrapDictionary as Dictionary | null) ??
    dictionaryCache.get(DEFAULT_LOCALE as Locale)?.dictionary ??
    ({} as Dictionary)

  const t = useMemoizedFn(
    (key: string, replacements?: Record<string, string | number>): string => {
      const text = dictionary[key]
      if (!text) return String(key)
      if (!replacements) return text
      return interpolate(text, replacements)
    }
  )

  return { t, lang }
}
